<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"
import type { components } from "@/assets/api/openapi"

const operationStore = useOperationStore()
const { startLoading, stopLoading } = useLoading()
const { getLanguageExaminationDataAh } = useAppApi()

const languageExamData =
  ref<components["schemas"]["FachsprachenpruefungReview"]>()
const REASON_MAX_LENGTH = 1000

const schema = yup.object().shape({
  result: yup
    .string()
    .required("Pflichtfeld")
    .test(
      "is-not-available",
      "Der Arbeitsschritt kann nicht abgeschlossen werden, solange das Ergebnis nicht vorliegt",
      yupTestOnlyOnCreate((value) => value !== "notAvailable")
    ),

  dateDetermination: yup.string().when("result", {
    is: (v: string) => v && v !== "notAvailable",
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),

  dateValidity: yup.string(),

  reason: yup.string().when("result", {
    is: "notRequired",
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () =>
    (operationStore.currentWorkStep?.data ?? {
      result: undefined,
    }) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

const resultOptions: IffRadioGroupOption[] = [
  {
    value: "notAvailable",
    label: "Liegt noch nicht vor",
  },
  {
    value: "approved",
    label: "Fachsprachenprüfung bestanden",
  },
  {
    value: "notRequired",
    label: "Fachsprachenprüfung nicht erforderlich",
  },
  {
    value: "canceled",
    label: "Fachsprachenprüfung abgebrochen ohne Ergebnis",
  },
]

const fetchData = async () => {
  if (operationStore.currentWorkStepId) {
    try {
      startLoading()

      const response = await getLanguageExaminationDataAh(
        operationStore.currentWorkStepId
      )

      if (response.status === 200 && response.data) {
        languageExamData.value = response.data
      }
    } catch {
      showError({
        statusCode: 500,
      })
    } finally {
      stopLoading()
    }
  }
}

const statusTechnicalLanguageTestDataFields = computed<DataField[]>(() => {
  return [
    {
      label: "Fachsprachenprüfung",
      value: statusText.value,
    },
    {
      label: "Gültig bis",
      value: languageExamData.value?.validityUntil ?? "-",
    },
  ]
})

const statusTextMap: Record<string, string> = {
  approved: "Bestanden am <DATE>",
  rejected: "Nicht bestanden am <DATE>",
  registered: "Angemeldet am <DATE>",
  unregistered: "Abgemeldet am <DATE>",
  absent: "Nicht angetreten am <DATE>",
}

const statusText = computed(() => {
  const value = languageExamData.value?.technicalLanguageExamination
  if (!value) return "-"
  return statusTextMap[value].replace(
    "<DATE>",
    languageExamData.value?.technicalLanguageExaminationDate ?? "?"
  )
})

watch(
  () => state.result,
  (value) => {
    if (value !== "approved") {
      state.dateValidity = undefined
    }

    if (value === "notAvailable") {
      state.dateDetermination = undefined
    }

    if (value !== "notRequired") {
      state.reason = undefined
    }
  }
)
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Organisation Fachsprachenprüfung"
        @call-update="fetchData"
      />
    </template>

    <IffContentSection headline="Stand der Fachsprachenprüfung">
      <IffDataFields :data="statusTechnicalLanguageTestDataFields" />
    </IffContentSection>

    <IffContentSection
      headline="Abschließendes Ergebnis der Fachsprachenprüfung"
      :is-last-child="true"
    >
      <IffRadioGroup
        legend="Ergebnis"
        name="result"
        v-model="state.result"
        :options="resultOptions"
        required
        aria-controls="date-determination-region date-validity-region reason-region"
      />
      <div id="date-determination-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            v-if="state.result && state.result !== 'notAvailable'"
            name="dateDetermination"
            label="Datum der Feststellung"
            required
            class="max-w-[200px]"
          >
            <template #hint>
              <UButton
                @click="
                  state.dateDetermination = new Date()
                    .toISOString()
                    .slice(0, 10)
                "
                icon="i-ic-outline-access-time"
                size="sm"
                color="gray"
                variant="ghost"
                :trailing="false"
                aria-label="Aktuelles Datum wählen"
                title="Aktuelles Datum wählen"
              />
            </template>
            <UInput
              v-model="state.dateDetermination"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
      <div id="date-validity-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            v-if="state.result && state.result === 'approved'"
            name="dateValidity"
            label="Gültig bis"
          >
            <UInput
              v-model="state.dateValidity"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
      <div id="reason-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            v-if="state.result === 'notRequired'"
            name="reason"
            label="Begründung"
            :help="`Noch ${
              REASON_MAX_LENGTH - (state.reason?.length ?? 0)
            } Zeichen verfügbar`"
            required
            class="max-w-[600px]"
          >
            <UTextarea
              v-model="state.reason"
              :maxlength="REASON_MAX_LENGTH"
              resize
              aria-required="true"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
